import { useRef, useState } from 'react';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST, BTN_SECONDARY, CopyField } from '~/components/workbench/tools/payments/ui';
import { checkManifestText, groupManifestErrors, type ManifestCheck } from '~/components/marketplace/developer-model';

/** A secret shown exactly once: copy it now, it can't be shown again. */
export function SecretReveal({
  label,
  value,
  note,
  onDone,
}: {
  label: string;
  value: string;
  note: string;
  onDone: () => void;
}) {
  return (
    <div className="space-y-2.5 rounded-xl border border-warning/30 bg-warning/[0.05] p-4">
      <div className="flex items-start gap-2">
        <div className="i-ph:warning mt-0.5 shrink-0 text-[14px] text-warning" />
        <p className="text-[12px] leading-relaxed text-warning">Copy this now. It won't be shown again. {note}</p>
      </div>
      <CopyField label={label} value={value} />
      <button type="button" onClick={onDone} className={BTN_GHOST}>
        I've saved it
      </button>
    </div>
  );
}

/** Paste or upload a manifest JSON; validated live with the SDK's validateManifest. */
export function ManifestInput({
  value,
  onChange,
  extraErrors = [],
  disabled,
}: {
  value: string;
  onChange: (text: string, check: ManifestCheck) => void;
  extraErrors?: string[];
  disabled?: boolean;
}) {
  const file = useRef<HTMLInputElement>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const check = checkManifestText(value);
  const errors = value.trim() ? [...check.errors, ...extraErrors] : [];
  const groups = groupManifestErrors(errors);

  const set = (text: string) => onChange(text, checkManifestText(text));

  const upload = async (f: File | undefined) => {
    setReadError(null);

    if (!f) {
      return;
    }

    if (f.size > 256 * 1024) {
      setReadError('That file is too large for a manifest (256 KB max).');
      return;
    }

    try {
      set(await f.text());
    } catch {
      setReadError('Could not read that file.');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-secondary">dunea.plugin, theme, template or skill .json</span>
        <button type="button" onClick={() => file.current?.click()} disabled={disabled} className={BTN_SECONDARY}>
          <div className="i-ph:upload-simple text-[13px]" />
          Upload
        </button>
        <input
          ref={file}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            void upload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      <textarea
        value={value}
        onChange={(e) => set(e.target.value)}
        placeholder='{ "schemaVersion": 1, "kind": "plugin", "id": "my-plugin", … }'
        spellCheck={false}
        rows={12}
        disabled={disabled}
        aria-label="Manifest JSON"
        className="ui-input-mono min-h-[180px] resize-y !text-[11.5px] leading-relaxed"
      />
      {readError && <p className="text-[11px] text-danger">{readError}</p>}
      {value.trim() &&
        (groups.length === 0 ? (
          <p className="flex items-center gap-1.5 text-[11.5px] text-success">
            <span className="i-ph:check-circle text-[13px]" />
            Valid {check.manifest?.kind} manifest: {check.manifest?.name} v{check.manifest?.version}
          </p>
        ) : (
          <div className="space-y-1.5 rounded-lg border border-danger/30 bg-danger/[0.05] p-3" role="alert">
            <p className="text-[11.5px] font-medium text-danger">
              {errors.length} {errors.length === 1 ? 'problem' : 'problems'} to fix
            </p>
            <ul className="space-y-1">
              {groups.map((g) => (
                <li key={g.path} className="text-[11.5px] leading-relaxed text-danger/90">
                  <code className="font-geist-mono text-[10.5px] text-primary">{g.path}</code> {g.messages.join('; ')}
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

/** Two-step destructive/irreversible confirm, inline. */
export function ConfirmButton({
  label,
  question,
  confirmLabel,
  onConfirm,
  busy,
  icon,
  danger,
}: {
  label: string;
  question: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
  icon?: string;
  danger?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  return confirming ? (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-[11.5px] text-secondary">{question}</span>
      <button
        type="button"
        onClick={() => {
          onConfirm();
          setConfirming(false);
        }}
        disabled={busy}
        className={classNames(BTN_SECONDARY, danger && '!border-danger/40 !text-danger')}
      >
        {confirmLabel}
      </button>
      <button type="button" onClick={() => setConfirming(false)} disabled={busy} className={BTN_GHOST}>
        Cancel
      </button>
    </span>
  ) : (
    <button type="button" onClick={() => setConfirming(true)} disabled={busy} className={BTN_GHOST}>
      {icon && <div className={classNames(icon, 'text-[13px]')} />}
      {label}
    </button>
  );
}
